import React, { useState, useEffect } from 'react';
import './PowerManagement.css';

// 電源管理ダッシュボードコンポーネント
const PowerManagement = () => {
  const [powerData, setPowerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // データ取得関数
    const fetchPowerData = async () => {
      try {
        setLoading(true);
        
        // モックデータ
        const mockData = {
          locations: [
            { 
              id: 1,
              name: '仙台北部ソーラーステーション', 
              status: 'optimal', 
              batteryLevel: 87, 
              powerOutput: 4.2, 
              dailyGeneration: 28.5,
              lastUpdate: '2025-03-18T08:15:22'
            },
            { 
              id: 2,
              name: '石巻ソーラーポイント', 
              status: 'warning', 
              batteryLevel: 42, 
              powerOutput: 2.1, 
              dailyGeneration: 15.8,
              lastUpdate: '2025-03-18T08:12:45'
            },
            { 
              id: 3,
              name: '気仙沼発電所', 
              status: 'critical', 
              batteryLevel: 15, 
              powerOutput: 0.8, 
              dailyGeneration: 5.2,
              lastUpdate: '2025-03-18T07:45:10'
            },
            { 
              id: 4,
              name: '名取ソーラーステーション', 
              status: 'optimal', 
              batteryLevel: 92, 
              powerOutput: 4.8, 
              dailyGeneration: 32.1,
              lastUpdate: '2025-03-18T08:17:33'
            },
            { 
              id: 5,
              name: '大崎発電所', 
              status: 'warning', 
              batteryLevel: 51, 
              powerOutput: 2.5, 
              dailyGeneration: 18.7,
              lastUpdate: '2025-03-18T08:05:19'
            }
          ],
          summary: {
            totalLocations: 5,
            optimalCount: 2,
            warningCount: 2,
            criticalCount: 1,
            totalPowerOutput: 14.4, // kW
            totalDailyGeneration: 100.3, // kWh
            averageBatteryLevel: 57.4, // %
            updateDate: '2025年3月18日 08:18'
          }
        };
        
        // データ取得の遅延をシミュレート
        setTimeout(() => {
          setPowerData(mockData);
          setLoading(false);
        }, 1000);
        
      } catch (err) {
        console.error('電源データの取得中にエラーが発生しました:', err);
        setError('データの取得に失敗しました。後でもう一度お試しください。');
        setLoading(false);
      }
    };

    fetchPowerData();
  }, []);

  // ステータスに基づいたクラス名を返す関数
  const getStatusClass = (status) => {
    switch(status) {
      case 'optimal':
        return 'status-optimal';
      case 'warning':
        return 'status-warning';
      case 'critical':
        return 'status-critical';
      default:
        return '';
    }
  };

  // ステータスの日本語表示を返す関数
  const getStatusText = (status) => {
    switch(status) {
      case 'optimal':
        return '正常';
      case 'warning':
        return '注意';
      case 'critical':
        return '警告';
      default:
        return '不明';
    }
  };

  // 日時をフォーマットする関数
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  };

  // ローディング中の表示
  if (loading) {
    return (
      <div className="power-loading">
        <p>データを読み込み中...</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <div className="power-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>再読み込み</button>
      </div>
    );
  }

  return (
    <div className="power-container">
      <div className="power-header">
        <h3>ソーラー電源管理ダッシュボード</h3>
        <p className="update-date">最終更新: {powerData.summary.updateDate}</p>
      </div>
      
      <div className="power-summary">
        <div className="summary-card">
          <h4>総発電量</h4>
          <p className="summary-value">{powerData.summary.totalPowerOutput} kW</p>
        </div>
        <div className="summary-card">
          <h4>本日の発電量</h4>
          <p className="summary-value">{powerData.summary.totalDailyGeneration} kWh</p>
        </div>
        <div className="summary-card">
          <h4>平均バッテリー残量</h4>
          <p className="summary-value">{powerData.summary.averageBatteryLevel}%</p>
        </div>
      </div>
      
      <div className="status-summary">
        <div className="status-card optimal">
          <h4>正常</h4>
          <p className="status-count">{powerData.summary.optimalCount}</p>
        </div>
        <div className="status-card warning">
          <h4>注意</h4>
          <p className="status-count">{powerData.summary.warningCount}</p>
        </div>
        <div className="status-card critical">
          <h4>警告</h4>
          <p className="status-count">{powerData.summary.criticalCount}</p>
        </div>
      </div>
      
      <div className="power-details">
        <h4>ソーラーステーション状況</h4>
        <div className="power-table-container">
          <table className="power-table">
            <thead>
              <tr>
                <th>ステーション名</th>
                <th>状態</th>
                <th>バッテリー</th>
                <th>発電量</th>
                <th>本日の発電量</th>
                <th>最終更新</th>
              </tr>
            </thead>
            <tbody>
              {powerData.locations.map((location) => (
                <tr key={location.id} className={getStatusClass(location.status)}>
                  <td>{location.name}</td>
                  <td>
                    <span className={`status-indicator ${getStatusClass(location.status)}`}></span>
                    {getStatusText(location.status)}
                  </td>
                  <td>
                    <div className="battery-indicator">
                      <div 
                        className={`battery-level ${location.batteryLevel < 30 ? 'low' : location.batteryLevel > 70 ? 'high' : 'medium'}`} 
                        style={{width: `${location.batteryLevel}%`}}
                      ></div>
                    </div>
                    <span className="battery-text">{location.batteryLevel}%</span>
                  </td>
                  <td>{location.powerOutput} kW</td>
                  <td>{location.dailyGeneration} kWh</td>
                  <td>{formatDateTime(location.lastUpdate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="power-footer">
        <p>総ステーション数: {powerData.summary.totalLocations}</p>
        <p>※ このデータはモックデータです</p>
      </div>
    </div>
  );
};

export default PowerManagement;
